import React, { useEffect, useState } from 'react';
import {
  Card, Form, Row, Col, Button,
} from 'react-bootstrap';
import { PlusLg } from 'react-bootstrap-icons';

import AsyncSelect from 'react-select/async';
import Required from '../Required';

import api from '../../api';

/**
 * Компонент для выбора нескольких пользователей в одну группу
 * @param data Массив пользователей
 * @param ctx Доп.параметры: заголовок и подзаголовок элемента,
 * роли (если пользователю надо назначить роль),
 * необходимость принудительно прописывать должность,
 * обязательность (если поле не обязательное, то можно удалить всех пользователей в группе),
 * доступ формы только на чтение
 * @param onChange Обработчик изменений в массиве из родительского элемента
 * @returns {JSX.Element}
 * @constructor
 */
export default function UserGroupEditor({ data, onChange, roles }) {

  const addCard = () => {
    const lastId = data
      .filter((d) => d.id <= 0)
      .map((d) => d.id)
      .sort((a, b) => a - b)
      [0];
    const newId = lastId === undefined ? 0 : lastId - 1;
    const newItem = { id: newId, userId: undefined, user: {} };
    if (roles) newItem.role = '';
    onChange(data.concat([newItem]));
  };

  const deleteCard = (itemId) => {
    onChange(data.filter((d) => d.id !== itemId));
  };

  const getItemLabel = (item) => {
    if (!item) return '';
    return (item.displayName || `${item.lastName || ''} ${item.firstName || ''} ${item.middleName || ''}`) + ` (${item.post})`;
  };

  // у Select варианты для выбора должны иметь специфическую структуру
  const mapOptionsToValues = (options) =>
  // value, label, isFixed, isDisabled
    options.map((option) => ({
      value: option.id,
      label: getItemLabel(option),
      user: option,
    }))
    ;

  // запрос к серверу, поиск по ФИО (можно с должностью, можно без)
  const getOptions = async (inputValue, callback) => {
    if (!inputValue || inputValue.length < 3) {
      return callback([]);
    }
    const res = await api.get(`/users/employees?name=${inputValue.trim()}`);
    callback(mapOptionsToValues(res));
  };

  // используется только когда есть массив ролей
  const onUserRoleChange = (itemId, value) => {
    const dataCopy = [...data];
    const changedItem = dataCopy.find((d) => d.id === itemId);
    changedItem.role = value;
    onChange(dataCopy);
  };

  const onUserItemChange = (user, selectedValue) => {
    const dataCopy = [...data];
    const changedItem = dataCopy.find((d) => d.id === user.id);
    if (selectedValue){
      changedItem.userId = selectedValue.value;
      changedItem.sAMAccountName = selectedValue.user.sAMAccountName;
      changedItem.displayName = `${selectedValue.user.lastName || ''} ${selectedValue.user.firstName || ''} ${selectedValue.user.middleName || ''}`;
      changedItem.post = selectedValue.user?.post;
      changedItem.email = selectedValue.user?.email;
    } else {
      changedItem.userId = null;
      changedItem.sAMAccountName = null;
      changedItem.displayName = null;
      changedItem.post = null;
      changedItem.email = null;
    }
    onChange(dataCopy);
  };

  return (
    <Card>
      <Card.Header className="p-4">
        <Card.Title style={{ marginBottom: 0 }}>Команда управления</Card.Title>
      </Card.Header>
        {data.map((item, index) => (
          <Card.Body>
            <Row key={item.id}>
              <Form.Group as={Col}>
                <Card className="mt-4">
                  <Card.Header style={cardHeaderStyling}>
                    <Button onClick={() => deleteCard(item.id)} variant="light" className="btn-close" size="sm" />
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Form.Group as={Col} md="6">
                        <Form.Label>
                          ФИО
                          <Required />
                        </Form.Label>
                        <div>
                          <AsyncSelect
                            noOptionsMessage={() => 'Ничего не найдено'}
                            loadingMessage={() => 'Поиск...'}
                            placeholder="Введите ФИО"
                            value={!item.sAMAccountName
                              ? null
                              : {
                                value: item.userId,
                                label: getItemLabel(item),
                              }}
                            defaultOptions={[]}
                            isClearable
                            loadOptions={getOptions}
                            onChange={(selectedValue, triggeredAction) => {
                              if (triggeredAction.action === 'clear') {
                                return onUserItemChange(item, null);
                              }
                              return onUserItemChange(item, selectedValue);
                            }}
                            components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
                          />
                        </div>
                        <Form.Control.Feedback type="invalid">Выберите из списка</Form.Control.Feedback>
                      </Form.Group>
                      {roles && (
                      <Form.Group as={Col} md="6">
                        <Form.Label>
                          Роль
                          <Required />
                        </Form.Label>
                        <Form.Control
                          required
                          as="select"
                          value={item.role}
                          onChange={(e) => onUserRoleChange(item.id, e.target.value, 'role')}
                        >
                          <option value="" />
                          {Object.keys(roles).map((k) => <option key={k} value={k}>{roles[k]}</option>)}
                        </Form.Control>
                        <Form.Control.Feedback type="invalid">Выберите из списка</Form.Control.Feedback>
                      </Form.Group>
                      )}
                    </Row>
                  </Card.Body>
                </Card>
              </Form.Group>
            </Row>
        </Card.Body>
      ))}
      <Row className='my-4 mx-3'>
        <Col md="2">
          <Button Button onClick={addCard} variant="outline-primary">
            <PlusLg className="me-1 mb-1" />
            Добавить
          </Button>
        </Col>
      </Row>
    </Card>
  );
}

const cardHeaderStyling = {
  display: 'flex', justifyContent: 'end', minHeight: 38,
};
